import { useTranslation } from 'react-i18next';

import { useNavigate } from '@hooks/useNavigation';
import Account from '@icons/Account.svg';
import Chevron from '@icons/Chevron.svg';
import Date from '@icons/Date.svg';
import Logout from '@icons/Logout.svg';
import Notification from '@icons/Notification.svg';
import Search from '@icons/Search.svg';
import Website from '@icons/Website.svg';

import { MenuModalProps } from '@components/Modal/interfaces';
import { MenuItem } from '@components/Modal/MenuModal/interfaces';

import {
  Action,
  BackdropMenu,
  MenuModalContainer,
  MenuOptionContainer,
  OptionContainer,
} from './styles';

/**
 * Renders menu items with consistent styling and behavior
 */
const renderMenuItems = (
  items: MenuItem[],
  onClick: (() => void) | undefined,
  goToPath: (path: string) => void,
) =>
  items.map((item, index) => {
    const Icon = item.icon;
    return (
      <MenuOptionContainer
        key={index}
        onClick={item.isLogout ? onClick || (() => {}) : () => goToPath(item.path || '')}>
        <OptionContainer>
          <Icon />
          <Action>{item.label}</Action>
        </OptionContainer>
        <Chevron />
      </MenuOptionContainer>
    );
  });

/**
 * MenuModal component that renders a modal menu, called from the header
 */
export const MenuModal = ({
  onClick,
  onClose,
  isOpen,
  onboardingFinished = true,
  role,
  isAdmin = false,
}: MenuModalProps) => {
  const { t } = useTranslation('common');
  const { goToPath } = useNavigate();
  if (!isOpen) return null;

  // Logout item that's always available
  const logoutItem: MenuItem = {
    icon: Logout,
    label: t('menu.logOut'),
    isLogout: true,
  };

  // If this is admin app, show only logout button
  if (isAdmin) {
    const menuItems: MenuItem[] = [logoutItem];
    return (
      <BackdropMenu onClick={onClose} $isAdmin={isAdmin}>
        <MenuModalContainer>
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <MenuOptionContainer
                key={index}
                onClick={item.isLogout ? onClick : () => goToPath(item.path || '')}>
                <OptionContainer>
                  <Icon />
                  <Action>{item.label}</Action>
                </OptionContainer>
                <Chevron />
              </MenuOptionContainer>
            );
          })}
        </MenuModalContainer>
      </BackdropMenu>
    );
  }

  // School-based menu item
  const schoolItem: MenuItem =
    role === 'school'
      ? { icon: Website, label: t('menu.schoolPage'), path: '/school/listing' }
      : {
        icon: Search,
        label: t('menu.overview'),
        path: `/${role}/overview`,
      };

  const menuItems: MenuItem[] = onboardingFinished
    ? role === 'school'
      ? [
        {
          icon: Date,
          label: t('menu.schoolAssignments'),
          path: `/${role}/shifts`,
        },
        schoolItem,
        {
          icon: Account,
          label: t('menu.profile'),
          path: `/${role}/profile`,
        },
        {
          icon: Notification,
          label: t('menu.schoolNotifications'),
          path: `/${role}/notifications`,
        },
        logoutItem,
      ]
      : [
        {
          icon: Date,
          label: t('menu.schoolAssignments'),
          path: `/${role}/shifts`,
        },
        schoolItem,
        {
          icon: Account,
          label: t('menu.profile'),
          path: `/${role}/profile`,
        },
        {
          icon: Notification,
          label: t('menu.teacherNotifications'),
          path: `/${role}/notifications`,
        },
        logoutItem,
      ]
    : [logoutItem];

  return (
    <BackdropMenu onClick={onClose}>
      <MenuModalContainer>{renderMenuItems(menuItems, onClick, goToPath)}</MenuModalContainer>
    </BackdropMenu>
  );
};
